import React, { useMemo } from "react";
import { generateText } from "@tiptap/core";
import { websiteBuilderHeadingRichTextExtensions, websiteBuilderParagraphRichTextExtensions } from "pages/secure/facility/websiteBuilder/editor/richText/extensions/websiteBuilderRichTextExtensions";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { isWebsiteBuilderElementContainerParentNode, isWebsiteBuilderElementParentNode, TWebsiteBuilderNode } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderNode";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderToolbarMenuView } from "../../WebsiteBuilderToolbarMenuView";
import { useWebsiteBuilderSectionMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/sectionMetadata/useWebsiteBuilderSectionMetadata";
import { useWebsiteBuilderContainerMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/containerMetadata/useWebsiteBuilderContainerMetadata";
import { useWebsiteBuilderElementMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/elementMetadata/useWebsiteBuilderElementMetadata";
import { LayersNode } from "./LayersNode";
import { websiteBuilderSelectNode } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { useWebsiteBuilderEditorContext } from "pages/secure/facility/websiteBuilder/editor/websiteBuilderEditorContext";
import { faFile, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { TWebsiteBuilderElement } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderElement";
import "./layersView.scss";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { TWebsiteBuilderSection, websiteBuilderGetSectionVisibility } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderSection";
import { TWebsiteBuilderSectionEntity } from "pages/secure/facility/websiteBuilder/editor/nodes/websiteBuilderSectionEntity";
import { TWebsiteBuilderEmbedProvider } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderEmbed";
import { useWebsiteBuilderGridCellMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/gridCellMetadata/useWebsiteBuilderGridCellMetadata";
import { useWebsiteBuilderTabMetadata } from "pages/secure/facility/websiteBuilder/editor/hooks/tabMetadata/useWebsiteBuilderTabMetadata";
import { websiteBuilderGridCellFilled } from "pages/secure/facility/websiteBuilder/editor/nodes/gridCell/websiteBuilderGridCell";
import { isWebsiteBuilderBox } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderBox";
import { cloneDeep } from "lodash";
import { isWebsiteBuilderGrid } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import { isWebsiteBuilderTabset } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTabset";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";
import { websiteBuilderFindIconAsset } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";

export function LayersView() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  
  const { activePageDomNodeRefMap, tabsetActiveTabMap, scrollContainerRef } = useWebsiteBuilderEditorContext();
  
  const header = useAppSelector(store => store.websiteBuilderStore.editor.header);
  const footer = useAppSelector(store => store.websiteBuilderStore.editor.footer);
  const pageMap = useAppSelector(store => store.websiteBuilderStore.editor.pageMap);
  const selectedNodeId = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode?.id);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const activePage = pageMap.get(activePageId);

  const sectionMetadata = useWebsiteBuilderSectionMetadata();
  const containerMetadata = useWebsiteBuilderContainerMetadata();
  const gridCellMetadata = useWebsiteBuilderGridCellMetadata();
  const tabMetadata = useWebsiteBuilderTabMetadata();
  const elementMetadata = useWebsiteBuilderElementMetadata();

  function getSortedSectionEntitiesByZIndex<T extends TWebsiteBuilderSectionEntity>(children: T[]): T[] {
    if (children == null) {
      return [];
    }

    const getZIndex = (node: TWebsiteBuilderSectionEntity) => node.type === "container" ? node.container.zIndex : node.element.zIndex;
    const compareZIndexes = (prev: TWebsiteBuilderSectionEntity, next: TWebsiteBuilderSectionEntity) => getZIndex(prev) - getZIndex(next);

    for (const child of children) {
      if (isWebsiteBuilderBox(child)) {
        child.children.sort(compareZIndexes);
      } else if (isWebsiteBuilderTabset(child)) {
        child.children.forEach(tab => {
          tab.children.sort(compareZIndexes);
        });
      }
    }
    
    return children.sort(compareZIndexes);
  }

  function getSortedSection<T extends TWebsiteBuilderSection>(node: T): T {
    if (node == null) {
      return null;
    }
    
    return {
      ...node,
      children: getSortedSectionEntitiesByZIndex(node.children),
    };
  }

  const sortedHeader: IWebsiteBuilderHeader = useMemo(() => {
    return getSortedSection(cloneDeep(header));
  }, [header]);

  const sortedRoot: IWebsiteBuilderRoot = useMemo(() => {
    if (activePage?.root == null) {
      return null;
    }

    const updatedRoot = cloneDeep(activePage.root);

    return {
      ...updatedRoot,
      children: updatedRoot.children.map(getSortedSection),
    };
  }, [activePage?.root]);

  const sortedFooter: IWebsiteBuilderFooter = useMemo(() => {
    return getSortedSection(cloneDeep(footer));
  }, [footer]);

  function getEmbedLabel(provider: TWebsiteBuilderEmbedProvider) {
    switch (provider) {
      case "google-calendar":
        return t("Google Calendar Embed");
      case "google-maps":
        return t("Google Maps Embed");
      case "youtube":
        return t("Youtube Embed");
      default:
        return elementMetadata.embed.title;
    }
  }

  function getElementLabel(node: TWebsiteBuilderElement) {
    const valueOrDefault = (value: string) => {
      return value != null && value.length > 0 ? value : elementMetadata[node.element.type].title;
    };

    switch (node.element.type) {
      case "button":
        return valueOrDefault(node.element.settings.label);
      case "divider":
        return valueOrDefault(null);
      case "embed":
        return getEmbedLabel(node.element.settings.provider);
      case "heading":
        return valueOrDefault(generateText(node.element.settings.content, websiteBuilderHeadingRichTextExtensions, { blockSeparator: " " }));
      case "icon": {
        const iconAsset = websiteBuilderFindIconAsset(iconAssets, node.element.settings.assetIdentity);

        return iconAsset?.title ? t("{{title}} Icon", { title: iconAsset.title }) : valueOrDefault(null);
      }
      case "image":
        return valueOrDefault(node.element.settings.alt);
      case "link":
        return valueOrDefault(node.element.settings.label);
      case "list":
        return valueOrDefault(null);
      case "logo":
        return valueOrDefault(node.element.settings.alt);
      case "navigation-menu":
        return valueOrDefault(null);
      case "paragraph":
        return valueOrDefault(generateText(node.element.settings.content, websiteBuilderParagraphRichTextExtensions, { blockSeparator: " " }));
      case "video":
        return valueOrDefault(null);
      default:
        return valueOrDefault(null);
    }
  }

  function getNodeLabel(node: TWebsiteBuilderNode): string {
    if (node.type === "root") {
      return activePage.title;
    } else if (node.type === "section") {
      return sectionMetadata[node.section.type].title;
    } else if (node.type === "container") {
      return containerMetadata[node.container.type].title;
    } else if (node.type === "grid-cell") {
      return gridCellMetadata.title;
    } else if (node.type === "tab") {
      return node.settings.title == null || node.settings.title.length === 0 ? tabMetadata.title : node.settings.title;
    } else if (node.type === "element") {
      return getElementLabel(node);
    } else {
      return "";
    }
  }

  function getNodeIcon(node: TWebsiteBuilderNode): IconDefinition {
    if (node.type === "root") {
      return faFile;
    } else if (node.type === "section") {
      return sectionMetadata[node.section.type].icon;
    } else if (node.type === "container") {
      return containerMetadata[node.container.type].icon;
    } else if (node.type === "grid-cell") {
      return gridCellMetadata.icon;
    } else if (node.type === "tab") {
      return tabMetadata.icon;
    } else if (node.type === "element") {
      return elementMetadata[node.element.type].icon;
    } else {
      return null;
    }
  }

  function handleSelect(
    node: TWebsiteBuilderNode,
    parentNode: TWebsiteBuilderNode,
    grandParentNode: TWebsiteBuilderNode,
    greatGrandParentNode: TWebsiteBuilderNode,
    selected: boolean,
  ) {
    if (
      node?.type !== "section" &&
      node?.type !== "container" &&
      node?.type !== "element"
    ) {
      return;
    }

    if (selected) {
      dispatch(
        websiteBuilderSelectNode({
          selectedNode: null,
        }),
      );

      return;
    }
    
    if (node.type === "section") {
      dispatch(
        websiteBuilderSelectNode({
          selectedNode: {
            type: "section",
            id: node.id,
            editingMode: {
              type: "none",
            },
          },
        }),
      );
    } else if (node.type === "container") {
      dispatch(
        websiteBuilderSelectNode({
          selectedNode: {
            type: "container",
            sectionId: parentNode.id,
            id: node.id,
            editingMode: {
              type: "none",
            },
          },
        }),
      );
    } else if (node.type === "element") {
      let sectionId: string = null;
      let containerId: string = null;
      let gridCellId: string = null;
      let tabId: string = null;

      if (parentNode?.type === "section" && grandParentNode?.type === "root") {
        sectionId = parentNode.id;
      } else if (isWebsiteBuilderElementContainerParentNode(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
        sectionId = grandParentNode.id;
        containerId = parentNode.id;
      } else if (parentNode?.type === "grid-cell" && isWebsiteBuilderGrid(grandParentNode) && greatGrandParentNode?.type === "section") {
        sectionId = greatGrandParentNode.id;
        containerId = grandParentNode.id;
        gridCellId = parentNode.id;
      } else if (parentNode?.type === "tab" && isWebsiteBuilderTabset(grandParentNode) && greatGrandParentNode?.type === "section") {
        sectionId = greatGrandParentNode.id;
        containerId = grandParentNode.id;
        tabId = parentNode.id;
      } else {
        return;
      }

      dispatch(
        websiteBuilderSelectNode({
          selectedNode: {
            type: "element",
            sectionId,
            containerId,
            gridCellId,
            tabId,
            id: node.id,
            editingMode: {
              type: "none",
            },
          },
        }),
      );
    }
    
    requestAnimationFrame(() => {
      const ref = activePageDomNodeRefMap.current.get(node.id);

      if (ref != null && scrollContainerRef.current != null) {
        ref.scrollIntoView({ behavior: "instant", block: "center" });
      }
    });
  }

  function renderNode(
    node: TWebsiteBuilderNode,
    parentNode: TWebsiteBuilderNode,
    grandParentNode: TWebsiteBuilderNode,
    greatGrandParentNode: TWebsiteBuilderNode,
    depth: number,
    ancestorsVisible: boolean,
  ): JSX.Element {
    const nextDepth = depth + 1;
    
    if (node?.type === "root") {
      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={false}
        >
          {renderNode(sortedHeader, node, null, null, nextDepth, ancestorsVisible)}
          {node.children.length > 0 ? (
            node.children.map(child => renderNode(child, node, null, null, nextDepth, ancestorsVisible))
          ) : null}
          {renderNode(sortedFooter, node, null, null, nextDepth, ancestorsVisible)}
        </LayersNode>
      );
    } else if (node?.type === "section" && parentNode?.type === "root") {
      const selected = selectedNodeId === node.id;

      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={selected}
          handleSelect={() => handleSelect(node, parentNode, null, null, selected)}
        >
          {node.children.length > 0 ? (
            node.children.map(child => renderNode(child, node, parentNode, null, nextDepth, ancestorsVisible && websiteBuilderGetSectionVisibility(node, parentNode)[deviceView]))
          ) : null}
        </LayersNode>
      );
    } else if (node?.type === "container" && parentNode?.type === "section" && grandParentNode?.type === "root") {
      const selected = selectedNodeId === node.id;

      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={selected}
          handleSelect={() => handleSelect(node, parentNode, grandParentNode, null, selected)}
        >
          {node.children.length > 0 ? (
            node.children.map(child => renderNode(child, node, parentNode, grandParentNode, nextDepth, ancestorsVisible && node.container.visibility[deviceView]))
          ) : null}
        </LayersNode>
      );
    } else if (node?.type === "grid-cell" && isWebsiteBuilderGrid(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
      const child = websiteBuilderGridCellFilled(node) ? renderNode(node.children[0], node, parentNode, grandParentNode, nextDepth, ancestorsVisible) : null;
      
      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={false}
        >
          {child}
        </LayersNode>
      );
    } else if (node?.type === "tab" && isWebsiteBuilderTabset(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
      const activeTabId = tabsetActiveTabMap.get(parentNode.id);

      const tabVisible = node.id === activeTabId;

      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={false}
        >
          {node.children.length > 0 ? (
            node.children.map(child => renderNode(child, node, parentNode, grandParentNode, nextDepth, ancestorsVisible && tabVisible))
          ) : null}
        </LayersNode>
      );
    } else if (node?.type === "element" && isWebsiteBuilderElementParentNode(parentNode)) {
      const selected = selectedNodeId === node.id;

      return (
        <LayersNode
          key={node.id}
          node={node}
          parentNode={parentNode}
          label={getNodeLabel(node)}
          icon={getNodeIcon(node)}
          depth={depth}
          ancestorsVisible={ancestorsVisible}
          selected={selected}
          handleSelect={() => handleSelect(node, parentNode, grandParentNode, greatGrandParentNode, selected)}
        >
          {null}
        </LayersNode>
      );
    } else {
      return null;
    }
  }

  function renderLayers() {
    const invalidPageSelected = sortedHeader == null || sortedRoot == null || sortedFooter == null;

    return (
      <div className="website-builder-layers-view">
        {invalidPageSelected ? <div>{t("Invalid page selected")}</div> : renderNode(sortedRoot, null, null, null, 1, true)}
      </div>
    );
  }

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Layers")}
      noSectionsPadding
      sections={[
        {
          grow: true,
          content: renderLayers(),
        },
      ]}
    />
  );
}